import { Link, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';

/**
 * MarketingNav — the global top navigation for all marketing pages.
 *
 * Layout: logo on the left (GuestLens), a single hamburger button on
 * the right. Clicking the button slides a full-height drawer in from
 * the right edge of the viewport, carrying the nav links. The drawer
 * is also closed by: clicking a link, clicking the backdrop, or
 * pressing Escape. Body scroll is locked while the drawer is open.
 *
 * Why a drawer instead of inline links? The brief is a quiet editorial
 * header — the page itself is the content, the chrome stays out of the
 * way. Inline nav would compete with the serif logo and the
 * pull-quote-style headings.
 */
const NAV_LINKS = [
  { to: '/cum-functioneaza', label: 'Cum funcționează' },
  { to: '/preturi', label: 'Prețuri' },
  { to: '/despre', label: 'Despre' },
  { to: '/misiune', label: 'Misiune' },
  { to: '/intrebari-frecvente', label: 'Întrebări' },
  { to: '/contact', label: 'Contact' },
] as const;

export function MarketingNav() {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  // Slightly compress the bar once the user has scrolled. Pure
  // visual nicety — the layout still works without it.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close on route change (a link click will navigate; the same effect
  // also catches programmatic navigations from elsewhere).
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  // Body scroll lock + Escape key, only while open.
  useEffect(() => {
    if (!open) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);

    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <>
      <header
        className={[
          'sticky top-0 z-30 w-full transition-all duration-300',
          scrolled
            ? 'border-b border-border/60 bg-bg/85 backdrop-blur-md shadow-sm'
            : 'border-b border-transparent bg-bg/0',
        ].join(' ')}
      >
        <div className="container-page flex items-center justify-between py-md">
          <Link
            to="/"
            className="font-serif text-2xl tracking-tight"
            style={{ fontVariationSettings: "'opsz' 144, 'SOFT' 100" }}
            aria-label="GuestLens — înapoi acasă"
          >
            GuestLens
          </Link>

          {/* Hamburger button. We render it as 3 stacked <span>s so the
              line thickness and gap are exact and the open-state morph
              into a clean X is purely CSS-controlled. */}
          <button
            type="button"
            aria-label={open ? 'Închide meniul' : 'Deschide meniul'}
            aria-expanded={open}
            aria-controls="marketing-drawer"
            onClick={() => setOpen((v) => !v)}
            className="relative -mr-2 inline-flex h-10 w-10 items-center justify-center"
          >
            <span className="sr-only">{open ? 'Închide meniul' : 'Deschide meniul'}</span>
            <span
              className="absolute left-1/2 top-1/2 block h-px w-6 -translate-x-1/2 bg-fg transition-transform duration-300"
              style={{
                transform: `translate(-50%, -50%) translateY(${open ? '0' : '-5px'}) rotate(${open ? 45 : 0}deg)`,
              }}
            />
            <span
              className="absolute left-1/2 top-1/2 block h-px w-6 -translate-x-1/2 bg-fg transition-opacity duration-300"
              style={{
                opacity: open ? 0 : 1,
                transform: 'translate(-50%, -50%)',
              }}
            />
            <span
              className="absolute left-1/2 top-1/2 block h-px w-6 -translate-x-1/2 bg-fg transition-transform duration-300"
              style={{
                transform: `translate(-50%, -50%) translateY(${open ? '0' : '5px'}) rotate(${open ? -45 : 0}deg)`,
              }}
            />
          </button>
        </div>
      </header>

      {/* Backdrop — a clickable layer that dims the page. Always
          present in the DOM so the transition is smooth; we just toggle
          opacity/pointer-events. */}
      <div
        onClick={() => setOpen(false)}
        aria-hidden
        className={[
          'fixed inset-0 z-40 bg-fg/30 backdrop-blur-sm transition-opacity duration-300',
          open ? 'opacity-100' : 'pointer-events-none opacity-0',
        ].join(' ')}
      />

      {/* Drawer — slides in from the right. min(420px, 88vw) keeps it
          readable on phones without hogging the whole screen on desktop. */}
      <aside
        id="marketing-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Meniu de navigare"
        className={[
          'fixed right-0 top-0 z-50 flex h-[100dvh] w-[min(420px,92vw)] flex-col border-l border-border bg-bg shadow-2xl transition-transform duration-300 ease-out',
          open ? 'translate-x-0' : 'translate-x-full',
        ].join(' ')}
      >
        {/* Drawer header: a small kicker on the left, a close button on
            the right. Mirrors the global header structure (logo area +
            action area) so the two don't feel unrelated. */}
        <div className="flex items-center justify-between border-b border-border px-lg py-md">
          <span className="font-mono text-[0.7rem] uppercase tracking-[0.22em] text-fg-soft">
            Cuprins
          </span>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Închide meniul"
            className="-mr-2 inline-flex h-10 w-10 items-center justify-center text-fg-soft hover:text-fg"
          >
            <span className="sr-only">Închide</span>
            <svg
              viewBox="0 0 24 24"
              width="22"
              height="22"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              aria-hidden
            >
              <path d="m6 6 12 12" />
              <path d="M6 18 18 6" />
            </svg>
          </button>
        </div>

        {/* Drawer body. Title block at the top, then the index list
            with a tighter gap so the two feel like one section. */}
        <div className="flex-1 overflow-y-auto px-lg pt-lg pb-xl">
          <h2
            className="font-serif text-3xl leading-[1.05] tracking-tight"
            style={{ fontVariationSettings: "'opsz' 144, 'SOFT' 100" }}
          >
            GuestLens
          </h2>

          <p className="kicker mt-xl mb-md">Navigare</p>
          <ol className="space-y-sm">
            {NAV_LINKS.map((l, i) => {
              const active = pathname === l.to || pathname.startsWith(l.to + '/');
              return (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    aria-current={active ? 'page' : undefined}
                    className={[
                      'group flex items-baseline gap-md border-b border-border py-sm transition-colors',
                      active ? 'border-fg' : 'hover:border-fg-soft',
                    ].join(' ')}
                  >
                    <span
                      className={[
                        'font-mono text-xs uppercase tracking-[0.22em] transition-colors',
                        active ? 'text-fg' : 'text-fg-soft group-hover:text-fg',
                      ].join(' ')}
                    >
                      0{i + 1}
                    </span>
                    <span
                      className={[
                        'font-serif text-2xl leading-[1.1] tracking-tight transition-colors',
                        active ? 'text-fg' : 'text-fg-soft group-hover:text-fg',
                      ].join(' ')}
                      style={{ fontVariationSettings: "'opsz' 144" }}
                    >
                      {l.label}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ol>
        </div>

        {/* Drawer footer: small contact block, plus a "back home" link
            so the user has a clear way out of the marketing context if
            they're on a deep page. */}
        <div className="border-t border-border px-lg py-md">
          <p className="font-mono text-[0.7rem] uppercase tracking-[0.22em] text-fg-soft">
            Contact
          </p>
          <a
            href="mailto:guestlens.events@gmail.com"
            className="mt-xs block text-sm text-fg hover:text-accent"
          >
            guestlens.events@gmail.com
          </a>
          <Link
            to="/"
            onClick={() => setOpen(false)}
            className="mt-md inline-flex items-center gap-xs font-mono text-xs uppercase tracking-[0.18em] text-fg-soft hover:text-fg"
          >
            <span aria-hidden>←</span>
            Înapoi acasă
          </Link>
        </div>
      </aside>
    </>
  );
}

/**
 * BackToTop — a small floating button that appears once the user has
 * scrolled past a threshold. Smooth-scrolls back to the top. Used on
 * long marketing pages so people don't get stuck at the footer.
 */
export function BackToTop({ threshold = 400 }: { threshold?: number }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > threshold);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [threshold]);

  return (
    <button
      type="button"
      aria-label="Înapoi sus"
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      className={[
        'fixed bottom-6 right-6 z-30 rounded-full border border-border bg-bg/90 p-3 shadow-md backdrop-blur transition-all',
        'hover:bg-bg-alt active:scale-95',
        visible
          ? 'opacity-100 translate-y-0 pointer-events-auto'
          : 'opacity-0 translate-y-2 pointer-events-none',
      ].join(' ')}
    >
      <svg
        viewBox="0 0 24 24"
        width="20"
        height="20"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M12 19V5" />
        <path d="m5 12 7-7 7 7" />
      </svg>
    </button>
  );
}
